import { useCallback, useState } from 'react';

import { showErrorAlert, showWarningAlert } from 'utils/AlertUtils';
import {
  isBlockedAction,
  overrideInvoiceTaxNumber,
  precheckInvoiceTaxNumbers,
} from 'utils/invoiceTaxApi';

/**
 * RAT-1913: the tax-registry check that runs before an invoice is enqueued.
 *
 * It has to happen here rather than inside the invoice run because every
 * merchant invoice is produced by a background job (RAT-1810) that cannot stop
 * to ask anything. The operator's answer is stored, and the backend gate picks
 * it up when the job reaches the invoice. The gate is what enforces the rule -
 * this hook only asks first, so the operator finds out now instead of finding a
 * failed job later.
 *
 * Shared by the merchant list and the merchant detail page so the flow exists
 * once.
 */
export const useInvoiceTaxGate = () => {
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);

  const closeDialog = useCallback(() => setDialog(null), []);

  /**
   * Check the recipients in `payload`, then either run `proceed` or open the
   * override dialog. `payload` is the same period + merchantIds object the
   * invoice request already carries.
   *
   * A preview never reaches the tax authority, so it is not checked at all.
   */
  const guardInvoiceIssue = useCallback(async (payload, proceed) => {
    const merchantIds = payload?.merchantIds ?? [];

    if (payload?.isPreview || merchantIds.length === 0) {
      proceed();
      return;
    }

    const rows = await precheckInvoiceTaxNumbers({
      merchantIds,
      year: Number(payload.year),
      quarter: payload.quarter,
      month: payload.month,
      endMonth: payload.endMonth,
    });

    // The pre-check itself failed. Carry on: the backend gate still refuses an
    // invoice it must refuse, and our own failed request is not the operator's
    // problem to solve.
    if (!rows) {
      proceed();
      return;
    }

    const blocked = rows.filter((row) => isBlockedAction(row.action));

    if (blocked.length === 0) {
      proceed();
      return;
    }

    // More than one merchant is selected, so there is no single invoice to
    // confirm - an override needs a reason per invoice (RAT-1914 AC6). The
    // blocked ones are named here so the operator finds out before the run, and
    // the run itself holds exactly these back and reports them per row under
    // Results in the Invoice Jobs tab.
    if (merchantIds.length > 1) {
      showWarningAlert(
        `${blocked.length} of ${merchantIds.length} merchants have a tax number the registry does not confirm and will not be invoiced: ${blocked
          .map((row) => row.merchantName)
          .join(
            ', ',
          )}. Fix the tax number and run the batch again, or issue those invoices one at a time. The Invoice Jobs tab lists each one under Results.`,
      );
      proceed();
      return;
    }

    setDialog({ row: blocked[0], payload, proceed });
  }, []);

  /** AC4: store the operator's authorisation, then let the invoice go ahead. */
  const confirmOverride = useCallback(
    async (reason) => {
      if (!dialog) {
        return;
      }

      setSaving(true);
      try {
        await overrideInvoiceTaxNumber({
          merchantId: dialog.row.merchantId,
          year: Number(dialog.payload.year),
          quarter: dialog.payload.quarter,
          month: dialog.payload.month,
          endMonth: dialog.payload.endMonth,
          reason,
        });
        const { proceed } = dialog;
        setDialog(null);
        proceed();
      } catch (error) {
        // Nothing is enqueued: without a stored authorisation the job would
        // only fail on the gate.
        showErrorAlert(
          error?.message ||
            'The authorisation could not be saved, so no invoice was issued.',
        );
      } finally {
        setSaving(false);
      }
    },
    [dialog],
  );

  return {
    guardInvoiceIssue,
    confirmOverride,
    closeDialog,
    overrideModalProps: {
      isOpen: !!dialog,
      loader: saving,
      merchantName: dialog?.row?.merchantName ?? '',
      taxNumber: dialog?.row?.taxNumber ?? '',
      message: dialog?.row?.message ?? '',
      registryLegalName: dialog?.row?.registryLegalName ?? '',
      // Straight from the backend: it is the side that knows a self-billed
      // invoice has no recipient flag to carry an override.
      canOverride: !!dialog?.row?.canOverride,
    },
  };
};

export default useInvoiceTaxGate;
